"use client";

/**
 * Auth provider component.
 *
 * Wraps the app tree and provides auth context. Blocks rendering of children
 * while the initial auth check runs, preventing flash of unauthenticated content.
 * Redirects to /login when the user is unauthenticated.
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";

import { useAuth } from "../hooks/use-auth";
import { forceSignOut } from "../lib/session";
import { AUTH_ONLY_PATHS, isNoAuthPath } from "@/shared/lib/auth-paths";
import type { AuthStatus, LoginCredentials, RegisterCredentials, User } from "../types";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (creds: LoginCredentials) => Promise<User>;
  register: (creds: RegisterCredentials) => Promise<User>;
  logout: () => Promise<void>;
  retry: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const pathname = usePathname();
  const isPublicPage = AUTH_ONLY_PATHS.has(pathname);
  // The design gallery renders with no session and no backend by design, so
  // it must bypass the gate ENTIRELY — including the server-unreachable
  // screen below, which would otherwise replace it whenever the API is down.
  const noAuth = isNoAuthPath(pathname);

  // Unauthenticated on a protected page → funnel through forceSignOut.
  // Hard navigation (not router.replace) clears stale React state — an
  // auth boundary should be a clean slate, not a soft route swap.
  useEffect(() => {
    if (auth.status === "unauthenticated" && !isPublicPage && !noAuth) {
      forceSignOut("no-cookie");
    }
  }, [auth.status, isPublicPage, noAuth]);

  if (noAuth) {
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
  }

  // Backend is unreachable — surface a real error screen with a retry.
  // Take precedence over the public-page branch so the login form doesn't
  // render with a useless "Failed to fetch" error on submit.
  if (auth.status === "server-unreachable") {
    return (
      <AuthContext.Provider value={auth}>
        <ServerUnreachableScreen onRetry={auth.retry} />
      </AuthContext.Provider>
    );
  }

  // On public pages (login, register), always render children
  if (isPublicPage) {
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
  }

  // While auth is loading, render nothing to prevent FOUC
  if (auth.status === "loading") {
    return (
      <AuthContext.Provider value={auth}>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>
        </div>
      </AuthContext.Provider>
    );
  }

  // If unauthenticated on a protected page, render nothing (redirect in useEffect)
  if (auth.status === "unauthenticated") {
    return (
      <AuthContext.Provider value={auth}>
        <div className="min-h-screen bg-background" />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

function ServerUnreachableScreen({ onRetry }: { onRetry: () => Promise<void> }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm space-y-4 text-center">
        <h1 className="text-lg font-semibold">Can&apos;t reach the server</h1>
        <p className="text-sm text-muted-foreground">
          The HammerHead backend isn&apos;t responding. Check your connection,
          or try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => void onRetry()}
          className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return ctx;
}
